import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { RepeatRuleState } from '@/types/repeat';

interface RepeatRuleDialogProps {
  open: boolean;
  initialState: RepeatRuleState | null;
  onClose: () => void;
  onSave: (state: RepeatRuleState) => void;
}

const weekdayOptions = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

const weekOfMonthOptions = [
  { label: 'First', value: 1 },
  { label: 'Second', value: 2 },
  { label: 'Third', value: 3 },
  { label: 'Fourth', value: 4 },
];

const monthOptions = Array.from({ length: 12 }).map((_, index) => ({
  value: index + 1,
  label: new Date(2024, index, 1).toLocaleString(undefined, { month: 'long' }),
}));

const repeatTypes: Array<{ id: RepeatRuleState['rule']['ruleType']; label: string }> = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'after_completion', label: 'After Completion' },
];

const afterCompletionUnits = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

const defaultState: RepeatRuleState = {
  enabled: true,
  leadTimeDays: 0,
  rule: {
    ruleType: 'daily',
    intervalValue: 1,
    skipWeekends: false,
    daysOfWeek: [1],
    dayOfMonth: 1,
    weekOfMonth: 1,
    monthOfYear: 1,
    maxOccurrences: null,
    untilDate: null,
    metadata: { pattern: 'date', intervalUnit: 'days' },
  },
};

export function RepeatRuleDialog({ open, initialState, onClose, onSave }: RepeatRuleDialogProps) {
  const [state, setState] = useState<RepeatRuleState>(() => hydrateState(initialState));

  useEffect(() => {
    if (open) {
      setState(hydrateState(initialState));
    }
  }, [initialState, open]);

  const intervalLabel = useMemo(() => {
    switch (state.rule.ruleType) {
      case 'daily':
        return state.rule.intervalValue === 1 ? 'day' : 'days';
      case 'weekly':
        return state.rule.intervalValue === 1 ? 'week' : 'weeks';
      case 'monthly':
        return state.rule.intervalValue === 1 ? 'month' : 'months';
      case 'yearly':
        return state.rule.intervalValue === 1 ? 'year' : 'years';
      case 'after_completion': {
        const unit = String(state.rule.metadata?.intervalUnit ?? 'days');
        return unit;
      }
      default:
        return 'days';
    }
  }, [state.rule.intervalValue, state.rule.metadata?.intervalUnit, state.rule.ruleType]);

  const endMode = state.rule.maxOccurrences
    ? 'count'
    : state.rule.untilDate
      ? 'date'
      : 'never';

  const isWeekdayPattern = state.rule.metadata?.pattern === 'weekday';

  const updateRule = (updates: Partial<RepeatRuleState['rule']>) => {
    setState((prev) => ({
      ...prev,
      rule: {
        ...prev.rule,
        ...updates,
      },
    }));
  };

  const updateMetadata = (updates: Record<string, unknown>) => {
    setState((prev) => ({
      ...prev,
      rule: {
        ...prev.rule,
        metadata: { ...(prev.rule.metadata ?? {}), ...updates },
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl space-y-5">
        <DialogHeader>
          <DialogTitle>Repeat Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Repeat this task</p>
            </div>
            <Switch checked={state.enabled} onCheckedChange={(checked) => setState((prev) => ({ ...prev, enabled: checked }))} />
          </div>
          <div className="space-y-2">
            <Label>Frequency</Label>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {repeatTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  className={cn(
                    'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                    state.rule.ruleType === type.id ? 'border-primary bg-primary/10 text-primary' : 'border-border/60 hover:border-border'
                  )}
                  onClick={() => updateRule({ ruleType: type.id })}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-[180px,1fr]">
            <div>
              <Label>Every</Label>
              <Input
                type="number"
                min={1}
                value={state.rule.intervalValue}
                onChange={(event) => updateRule({ intervalValue: Math.max(1, Number(event.target.value) || 1) })}
              />
            </div>
            <div className="flex items-end">
              <p className="text-sm text-muted-foreground">
                {state.rule.ruleType === 'after_completion' ? 'After completion repeat every' : 'Repeat every'}{' '}
                <span className="font-semibold">{intervalLabel}</span>
              </p>
            </div>
          </div>
          {state.rule.ruleType === 'daily' && (
            <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
              <div>
                <p className="text-sm font-medium">Skip weekends</p>
                <p className="text-xs text-muted-foreground">Don’t schedule repeats on Saturdays or Sundays.</p>
              </div>
              <Switch
                checked={Boolean(state.rule.skipWeekends)}
                onCheckedChange={(checked) => updateRule({ skipWeekends: checked })}
              />
            </div>
          )}
          {state.rule.ruleType === 'weekly' && (
            <div className="space-y-2">
              <Label>Days of the week</Label>
              <div className="flex flex-wrap gap-2">
                {weekdayOptions.map((day) => {
                  const active = state.rule.daysOfWeek?.includes(day.value) ?? false;
                  return (
                    <button
                      key={day.value}
                      type="button"
                      className={cn(
                        'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                      )}
                      onClick={() => {
                        const next = new Set(state.rule.daysOfWeek ?? []);
                        if (next.has(day.value)) {
                          next.delete(day.value);
                        } else {
                          next.add(day.value);
                        }
                        updateRule({ daysOfWeek: Array.from(next).sort() });
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {state.rule.ruleType === 'monthly' && (
            <div className="space-y-3 rounded-2xl border border-border/60 p-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  className={cn(
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-medium',
                    !isWeekdayPattern ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                  )}
                  onClick={() => updateMetadata({ pattern: 'date' })}
                >
                  On day #{state.rule.dayOfMonth}
                </button>
                <button
                  type="button"
                  className={cn(
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-medium',
                    isWeekdayPattern ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                  )}
                  onClick={() => updateMetadata({ pattern: 'weekday' })}
                >
                  On weekday
                </button>
              </div>
              {!isWeekdayPattern ? (
                <div>
                  <Label>Day of month</Label>
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    value={state.rule.dayOfMonth ?? 1}
                    onChange={(event) => updateRule({ dayOfMonth: clampDay(Number(event.target.value)) })}
                  />
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>Week</Label>
                    <Select
                      value={String(state.rule.weekOfMonth ?? 1)}
                      onValueChange={(value) => updateRule({ weekOfMonth: Number(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {weekOfMonthOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Weekday</Label>
                    <Select
                      value={String(state.rule.daysOfWeek?.[0] ?? 1)}
                      onValueChange={(value) => updateRule({ daysOfWeek: [Number(value)] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {weekdayOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          {state.rule.ruleType === 'yearly' && (
            <div className="space-y-3 rounded-2xl border border-border/60 p-3">
              <div>
                <Label>Month</Label>
                <Select
                  value={String(state.rule.monthOfYear ?? 1)}
                  onValueChange={(value) => updateRule({ monthOfYear: Number(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((month) => (
                      <SelectItem key={month.value} value={String(month.value)}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Day of month</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={state.rule.dayOfMonth ?? 1}
                  onChange={(event) => updateRule({ dayOfMonth: clampDay(Number(event.target.value)) })}
                />
              </div>
            </div>
          )}
          {state.rule.ruleType === 'after_completion' && (
            <div className="space-y-2">
              <Label>Interval unit</Label>
              <div className="grid grid-cols-2 gap-2">
                {afterCompletionUnits.map((unit) => (
                  <button
                    key={unit.value}
                    type="button"
                    className={cn(
                      'rounded-xl border px-3 py-2 text-sm font-medium',
                      state.rule.metadata?.intervalUnit === unit.value ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                    )}
                    onClick={() => updateMetadata({ intervalUnit: unit.value })}
                  >
                    {unit.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Lead time (days)</Label>
              <Input
                type="number"
                min={0}
                value={state.leadTimeDays ?? 0}
                onChange={(event) => setState((prev) => ({ ...prev, leadTimeDays: Math.max(0, Number(event.target.value) || 0) }))}
              />
            </div>
            <div>
              <Label>Ends</Label>
              <div className="flex flex-wrap gap-2 pt-2 text-sm">
                {['never', 'count', 'date'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={cn(
                      'flex-1 rounded-xl border px-3 py-1.5 capitalize',
                      endMode === mode ? 'border-primary bg-primary/10 text-primary' : 'border-border/60'
                    )}
                    onClick={() => {
                      if (mode === 'never') {
                        updateRule({ maxOccurrences: null, untilDate: null });
                      } else if (mode === 'count') {
                        updateRule({ maxOccurrences: state.rule.maxOccurrences ?? 5, untilDate: null });
                      } else {
                        updateRule({ untilDate: state.rule.untilDate ?? new Date().toISOString().slice(0, 10), maxOccurrences: null });
                      }
                    }}
                  >
                    {mode === 'never' ? 'Never' : mode === 'count' ? 'After # times' : 'On date'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {endMode === 'count' && (
            <div>
              <Label>Occurrences</Label>
              <Input
                type="number"
                min={1}
                value={state.rule.maxOccurrences ?? 1}
                onChange={(event) => updateRule({ maxOccurrences: Math.max(1, Number(event.target.value) || 1) })}
              />
            </div>
          )}
          {endMode === 'date' && (
            <div>
              <Label>End date</Label>
              <Input
                type="date"
                value={state.rule.untilDate ?? ''}
                onChange={(event) => updateRule({ untilDate: event.target.value || null })}
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSave(state)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function hydrateState(initial: RepeatRuleState | null): RepeatRuleState {
  if (!initial) {
    return { ...defaultState, rule: { ...defaultState.rule, metadata: { ...defaultState.rule.metadata } } };
  }
  return {
    enabled: initial.enabled ?? true,
    leadTimeDays: initial.leadTimeDays ?? 0,
    rule: {
      ...defaultState.rule,
      ...initial.rule,
      metadata: { ...defaultState.rule.metadata, ...(initial.rule.metadata ?? {}) },
    },
  };
}

function clampDay(value: number) {
  if (Number.isNaN(value) || value < 1) {
    return 1;
  }
  if (value > 31) {
    return 31;
  }
  return value;
}
